import { type JSX, useRef, useState } from 'react';

import type { Tile } from '@schema/tiles.js';

const AGENT_ACTIONS = ['reboot', 'clearAppData', 'runScript', 'syncNow'] as const;
type AgentAction = (typeof AGENT_ACTIONS)[number];

/**
 * Headers the sample CSV ships with — the parser is case-insensitive
 * but the sample uses these exact names so admins can copy/paste an
 * existing spreadsheet column list without renaming.
 */
const CSV_HEADERS = [
  'type',
  'label',
  'packageName',
  'url',
  'action',
  'openExternal',
  'externalTab',
] as const;

const SAMPLE_CSV = [
  CSV_HEADERS.join(','),
  'app,Chrome,com.android.chrome,,,,',
  'app,Settings,com.android.settings,,,,',
  'web,Company Portal,,https://portal.example.com,,false,',
  'web,Help Desk,,https://help.example.com,,true,new',
  'web,Kiosk Docs,,https://docs.example.com,,true,same',
  'agentAction,Reboot device,,,reboot,,',
  'agentAction,Sync now,,,syncNow,,',
  'spacer,,,,,,',
  '',
].join('\n');

export interface TileCsvImportProps {
  /** Called once per successfully-parsed row. */
  onAddTile: (tile: Tile) => void;
  /** Disable the upload button (e.g. grid at capacity). */
  disabled?: boolean;
  /**
   * Number of free cells. Rows beyond this count are skipped with a
   * "grid full" warning so the user knows what got dropped.
   */
  freeCapacity: number;
}

interface ImportFeedback {
  ok: number;
  skipped: number;
  errors: string[];
}

/**
 * CSV bulk-import for the tile grid. Two buttons:
 *
 *   - "Download sample CSV" — saves a starter file with the headers
 *     pre-filled and one example row per tile type.
 *   - "Upload CSV" — opens a file picker, parses the file, appends
 *     each valid row as a tile via `onAddTile`. Reports a summary
 *     (added / skipped / per-row errors) below the buttons.
 *
 * The parser is intentionally small (~50 lines): it handles quoted
 * fields and embedded commas but skips RFC-4180 edge cases like
 * escaped quotes inside a quoted field. Admins exporting from Excel
 * or Numbers should be fine; bring-your-own-quoting CSVs may need
 * cleanup. The sample CSV demonstrates the supported shape.
 */
export function TileCsvImport({ onAddTile, disabled, freeCapacity }: TileCsvImportProps): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [feedback, setFeedback] = useState<ImportFeedback | null>(null);

  function downloadSample(): void {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mobilauncher-tiles-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleFile(file: File): Promise<void> {
    setFeedback(null);
    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      setFeedback({ ok: 0, skipped: 0, errors: [err instanceof Error ? err.message : 'read failed'] });
      return;
    }
    const { tiles, errors } = parseTilesCsv(text);
    const fits = tiles.slice(0, Math.max(0, freeCapacity));
    const skipped = tiles.length - fits.length;
    for (const t of fits) onAddTile(t);
    setFeedback({ ok: fits.length, skipped, errors });
  }

  return (
    <div className="tile-csv-import">
      <button
        type="button"
        className="builder__btn"
        onClick={() => fileRef.current?.click()}
        disabled={disabled === true}
        data-testid="tile-csv-upload"
      >
        ⬆ Upload CSV
      </button>
      <button
        type="button"
        className="builder__btn builder__btn--ghost"
        onClick={downloadSample}
        data-testid="tile-csv-sample"
      >
        ⬇ Download sample
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="text/csv,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
        data-testid="tile-csv-input"
      />
      {feedback && (
        <div
          className={
            feedback.errors.length > 0 || feedback.skipped > 0
              ? 'tile-csv-import__feedback tile-csv-import__feedback--warn'
              : 'tile-csv-import__feedback tile-csv-import__feedback--ok'
          }
          role="status"
        >
          <strong>
            Added {feedback.ok}{' '}
            {feedback.ok === 1 ? 'tile' : 'tiles'}
            {feedback.skipped > 0 && ` · skipped ${feedback.skipped} (grid full)`}
            {feedback.errors.length > 0 && ` · ${feedback.errors.length} row error${feedback.errors.length === 1 ? '' : 's'}`}
          </strong>
          {feedback.errors.length > 0 && (
            <ul className="tile-csv-import__errors">
              {feedback.errors.slice(0, 8).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
              {feedback.errors.length > 8 && (
                <li>…and {feedback.errors.length - 8} more</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ───── CSV parsing ───── */

interface ParseResult {
  tiles: Tile[];
  errors: string[];
}

function parseTilesCsv(text: string): ParseResult {
  const tiles: Tile[] = [];
  const errors: string[] = [];

  const rows = parseCsv(text);
  if (rows.length === 0) {
    return { tiles, errors: ['CSV is empty.'] };
  }

  const header = rows[0]!.map((h) => h.trim().toLowerCase());
  const idx: Record<string, number> = Object.create(null);
  for (let i = 0; i < header.length; i += 1) {
    const name = header[i]!;
    idx[name] = i;
  }

  if (idx['type'] === undefined || idx['label'] === undefined) {
    return {
      tiles,
      errors: ['CSV must include at least `type` and `label` columns.'],
    };
  }

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r]!;
    if (row.every((c) => c.trim() === '')) continue; // blank line
    const lineNo = r + 1;
    const get = (k: string): string => (idx[k] === undefined ? '' : (row[idx[k]!] ?? '').trim());

    const rawType = get('type');
    const type = rawType.toLowerCase();
    const label = get('label');

    try {
      if (type === 'app') {
        if (label === '') throw new Error('label required');
        const packageName = get('packagename');
        if (packageName === '') throw new Error('packageName required for app tiles');
        tiles.push({ type: 'app', id: crypto.randomUUID(), label, packageName });
      } else if (type === 'web') {
        if (label === '') throw new Error('label required');
        const url = get('url');
        if (url === '') throw new Error('url required for web tiles');
        const openExternal = parseBool(get('openexternal'));
        const tabRaw = get('externaltab').toLowerCase();
        if (tabRaw !== '' && tabRaw !== 'new' && tabRaw !== 'same') {
          throw new Error('externalTab must be "new", "same", or empty');
        }
        tiles.push({
          type: 'web',
          id: crypto.randomUUID(),
          label,
          url,
          openExternal,
          ...(tabRaw === 'new' || tabRaw === 'same' ? { externalTab: tabRaw } : {}),
        });
      } else if (type === 'agentaction') {
        if (label === '') throw new Error('label required');
        const action = get('action') as AgentAction;
        if (!AGENT_ACTIONS.includes(action)) {
          throw new Error(
            `action must be one of ${AGENT_ACTIONS.join(', ')} (got "${action}")`,
          );
        }
        tiles.push({ type: 'agentAction', id: crypto.randomUUID(), label, action });
      } else if (type === 'spacer') {
        tiles.push({ type: 'spacer', id: crypto.randomUUID(), label: label || 'spacer' });
      } else {
        throw new Error(`unknown type "${rawType}" (expected app | web | agentAction | spacer)`);
      }
    } catch (err) {
      errors.push(`Row ${String(lineNo)}: ${err instanceof Error ? err.message : 'invalid'}`);
    }
  }

  return { tiles, errors };
}

/**
 * Minimal CSV parser — handles quoted fields with embedded commas. Does
 * NOT handle escaped quotes inside a quoted field ("" → "); admins who
 * need that should pre-process their export. RFC-4180 edge cases left
 * out deliberately to keep the parser small and predictable.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      // Skip \r\n pair by consuming \n on the next iteration.
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      field = '';
      if (row.some((c) => c.length > 0)) rows.push(row);
      row = [];
      continue;
    }
    field += ch;
  }
  // Trailing field if file doesn't end with newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.some((c) => c.length > 0)) rows.push(row);
  }
  return rows;
}

function parseBool(s: string): boolean {
  const v = s.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}
